import { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Image, Video, Upload, X, Link2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Media Upload Component for Posts
 * Supports both local file upload and URL input
 */
const PostMediaUploader = ({ 
  images = [], 
  videoUrl = '', 
  onImagesChange, 
  onVideoChange,
  maxImages = 9 
}) => {
  const { t } = useTranslation();
  const imageInputRef = useRef(null);
  const videoInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [showUrlInput, setShowUrlInput] = useState(false);
  const [urlInput, setUrlInput] = useState('');
  const [urlType, setUrlType] = useState('image'); // 'image' or 'video'
  const token = localStorage.getItem('token');

  // Get full URL for display
  const getFullUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    return `${BACKEND_URL}${url}`;
  };

  // Handle local image upload
  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    if (images.length + files.length > maxImages) {
      toast.error(t('community.maxImagesError', { max: maxImages }));
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    try {
      const uploadedUrls = [];
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const formData = new FormData();
        formData.append('file', file);

        const res = await axios.post(`${API}/upload/image`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
            'Authorization': `Bearer ${token}`
          },
          onUploadProgress: (progressEvent) => {
            const progress = Math.round(
              ((i + progressEvent.loaded / progressEvent.total) / files.length) * 100
            );
            setUploadProgress(progress);
          }
        });

        uploadedUrls.push(res.data.url);
      }

      onImagesChange([...images, ...uploadedUrls]);
      toast.success(t('community.uploadSuccess', { count: uploadedUrls.length }));
    } catch (err) {
      console.error('Upload error:', err);
      toast.error(err.response?.data?.detail || t('community.uploadFailed'));
    } finally {
      setUploading(false);
      setUploadProgress(0);
      if (imageInputRef.current) {
        imageInputRef.current.value = '';
      }
    }
  };

  // Handle local video upload
  const handleVideoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (videoUrl) {
      toast.error(t('community.onlyOneVideo'));
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const res = await axios.post(`${API}/upload/video`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${token}`
        },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded / progressEvent.total) * 100);
          setUploadProgress(progress);
        }
      });

      onVideoChange(res.data.url);
      toast.success(t('community.videoUploadSuccess'));
    } catch (err) {
      console.error('Video upload error:', err);
      toast.error(err.response?.data?.detail || t('community.videoUploadFailed'));
    } finally {
      setUploading(false);
      setUploadProgress(0);
      if (videoInputRef.current) {
        videoInputRef.current.value = '';
      }
    }
  };

  // Handle URL input
  const handleUrlSubmit = () => {
    if (!urlInput.trim()) return;

    if (urlType === 'image') {
      if (images.length >= maxImages) {
        toast.error(t('community.maxImagesError', { max: maxImages }));
        return;
      }
      onImagesChange([...images, urlInput.trim()]);
    } else {
      if (videoUrl) {
        toast.error(t('community.onlyOneVideo'));
        return;
      }
      onVideoChange(urlInput.trim());
    }

    setUrlInput('');
    setShowUrlInput(false);
  };

  // Remove image
  const removeImage = (index) => {
    const newImages = images.filter((_, i) => i !== index);
    onImagesChange(newImages);
  };

  // Remove video
  const removeVideo = () => {
    onVideoChange('');
  };

  return (
    <div className="space-y-4">
      {/* Upload Buttons - Mobile optimized with label for better touch support */}
      <div className="grid grid-cols-3 gap-2 sm:flex sm:flex-wrap">
        {/* Image Upload - Using label for better mobile support */}
        <label 
          className={`inline-flex flex-col sm:flex-row items-center justify-center gap-1 sm:gap-2 px-3 py-3 sm:py-2 border rounded-md text-sm font-medium transition-colors cursor-pointer
            ${uploading || images.length >= maxImages 
              ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
              : 'bg-white hover:bg-gray-50 border-gray-300 text-gray-700'}`}
        >
          <input
            ref={imageInputRef}
            type="file"
            accept="image/*"
            multiple
            onChange={handleImageUpload}
            className="sr-only"
            disabled={uploading || images.length >= maxImages}
            data-testid="post-image-input"
          />
          <Image className="w-5 h-5 sm:w-4 sm:h-4" />
          <span className="text-xs sm:text-sm">{t('community.addImage')}</span>
        </label>

        {/* Video Upload - Using label for better mobile support */}
        <label 
          className={`inline-flex flex-col sm:flex-row items-center justify-center gap-1 sm:gap-2 px-3 py-3 sm:py-2 border rounded-md text-sm font-medium transition-colors cursor-pointer
            ${uploading || !!videoUrl 
              ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
              : 'bg-white hover:bg-gray-50 border-gray-300 text-gray-700'}`}
        >
          <input
            ref={videoInputRef}
            type="file"
            accept="video/*"
            onChange={handleVideoUpload}
            className="sr-only"
            disabled={uploading || !!videoUrl}
            data-testid="post-video-input"
          />
          <Video className="w-5 h-5 sm:w-4 sm:h-4" />
          <span className="text-xs sm:text-sm">{t('community.addVideo')}</span>
        </label>

        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => setShowUrlInput(!showUrlInput)}
          disabled={uploading}
          data-testid="url-input-toggle-btn"
          className="flex-col h-auto py-3 sm:flex-row sm:h-9 sm:py-0"
        >
          <Link2 className="w-5 h-5 sm:w-4 sm:h-4 sm:mr-2" />
          <span className="text-xs sm:text-sm mt-1 sm:mt-0">{t('community.addUrl')}</span>
        </Button>
      </div>

      {/* URL Input */}
      {showUrlInput && (
        <div className="flex flex-col gap-2 p-3 bg-gray-50 rounded-lg">
          <div className="flex gap-2">
            <select
              value={urlType}
              onChange={(e) => setUrlType(e.target.value)}
              className="border rounded px-2 py-2 text-sm flex-shrink-0"
            >
              <option value="image">{t('community.imageUrl')}</option>
              <option value="video">{t('community.videoUrl')}</option>
            </select>
            <Button type="button" variant="ghost" size="sm" onClick={() => setShowUrlInput(false)} className="ml-auto">
              <X className="w-4 h-4" />
            </Button>
          </div>
          <Input
            value={urlInput}
            onChange={(e) => setUrlInput(e.target.value)}
            placeholder={urlType === 'image' ? 'https://example.com/image.jpg' : 'https://example.com/video.mp4'}
            className="text-base"
            onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleUrlSubmit())}
          />
          <Button type="button" size="sm" onClick={handleUrlSubmit} className="w-full">
            {t('common.add')}
          </Button>
        </div>
      )}

      {/* Upload Progress */}
      {uploading && (
        <div className="space-y-2">
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <Upload className="w-4 h-4 animate-pulse" />
            {t('community.uploading')} {uploadProgress}%
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-green-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${uploadProgress}%` }}
            />
          </div>
        </div>
      )}

      {/* Image Previews */}
      {images.length > 0 && (
        <div className="space-y-2">
          <p className="text-sm text-gray-600">
            {t('community.images')} ({images.length}/{maxImages})
          </p>
          <div className="grid grid-cols-3 gap-2">
            {images.map((img, index) => (
              <div key={index} className="relative aspect-square rounded-lg overflow-hidden bg-gray-100">
                <img
                  src={getFullUrl(img)}
                  alt={`Upload ${index + 1}`}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><rect fill="%23f3f4f6" width="100" height="100"/><text x="50" y="50" dominant-baseline="middle" text-anchor="middle" fill="%239ca3af">Error</text></svg>';
                  }}
                />
                <button
                  type="button"
                  onClick={() => removeImage(index)}
                  className="absolute top-1 right-1 w-6 h-6 bg-black/60 rounded-full flex items-center justify-center text-white hover:bg-black/80 transition-colors"
                  data-testid={`remove-image-${index}`}
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Video Preview */}
      {videoUrl && (
        <div className="space-y-2">
          <p className="text-sm text-gray-600">{t('community.video')}</p>
          <div className="relative rounded-lg overflow-hidden bg-gray-100">
            <video
              src={getFullUrl(videoUrl)}
              controls
              className="w-full max-h-64 object-contain"
            />
            <button
              type="button"
              onClick={removeVideo}
              className="absolute top-2 right-2 w-8 h-8 bg-black/60 rounded-full flex items-center justify-center text-white hover:bg-black/80 transition-colors"
              data-testid="remove-video"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}

      {/* Helper text */}
      <p className="text-xs text-gray-500">
        {t('community.mediaHelp', { maxImages, imageSize: '10MB', videoSize: '100MB' })}
      </p>
    </div>
  );
};

export default PostMediaUploader;
